import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useToast } from "@design";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowConnectorConfig, FlowResponse, FlowStepConfig } from "@/api";
import { getFlowQueryKey, updateFlowMutation } from "@/api";
import { useLeaveGuard } from "@/components/build/useLeaveGuard";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Text } from "@/components/Text";
import {
  buildTreeEdges,
  collectSubtree,
  computeTreeLayout,
  findLastLeaf,
  generateNodeId,
  NODE_X,
  removeStep,
  removeSubtree,
  type StepNode,
  type StepNodeData,
  type StepTree,
  saveOrder,
  serializeSteps,
  treeFromFlow,
  treeSnapshot,
} from "./flowEditorModel";
import { AgentPicker, StepConfig } from "./StepConfigPanel";
import { StepNodeView } from "./StepNodeView";
import { StepsEditorPanels } from "./StepsEditorPanels";

/**
 * The visual step editor (React Flow v12). Ported from the old UI's
 * FlowBuilder, adapted to design tokens. The tree model + layout live in
 * flowEditorModel.ts (pure, unit-tested); this is the canvas; the node and
 * the step config panel are in StepNodeView/StepConfigPanel. Saves the whole
 * steps/connectors array via the flow PUT, then reloads the canvas from what
 * was saved. Tracks unsaved changes against the last saved snapshot and asks
 * before leaving with them (`onDirtyChange` lets the page react, e.g. Run).
 */
export function FlowStepsEditor({
  flow,
  onDirtyChange,
}: Readonly<{ flow: FlowResponse; onDirtyChange?: (dirty: boolean) => void }>) {
  return (
    <ReactFlowProvider>
      <FlowStepsEditorInner flow={flow} onDirtyChange={onDirtyChange} />
    </ReactFlowProvider>
  );
}

const nodeTypes = { step: StepNodeView };

function FlowStepsEditorInner({
  flow,
  onDirtyChange,
}: Readonly<{ flow: FlowResponse; onDirtyChange?: (dirty: boolean) => void }>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<StepNode>([]);
  const [edges, setEdges] = useEdgesState<Edge>([]);
  const [parentMap, setParentMap] = useState<Map<string, string | null>>(new Map());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickingAgent, setPickingAgent] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string; count: number } | null>(null);
  const initialized = useRef(false);
  // The current tree, for the node handlers (created once, so they can't close over state).
  const latest = useRef<StepTree>({ nodes, parentMap });
  latest.current = { nodes, parentMap };

  const dirty = savedSnapshot !== null && treeSnapshot({ nodes, parentMap }) !== savedSnapshot;
  const leaveGuard = useLeaveGuard(dirty);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const newStepNode = (id: string, index: number, h: typeof handlers.current): StepNode => ({
    id,
    type: "step",
    position: { x: NODE_X, y: 0 },
    data: { requiresReview: false, config: {}, label: `${t("editor.stepConfig")} ${index}`, ...h },
  });

  const addStep = (parentId: string | null) => {
    const newId = generateNodeId();
    setParentMap((prev) => new Map(prev).set(newId, parentId));
    setNodes((nds) => [...nds, newStepNode(newId, nds.length + 1, handlers.current)]);
  };

  // Canvas nodes and the tree always change together, so every step shown is a step that saves.
  const applyTree = (tree: StepTree) => {
    setNodes(tree.nodes);
    setParentMap(tree.parentMap);
    setSelectedId((prev) => (prev && tree.nodes.some((n) => n.id === prev) ? prev : null));
  };

  const handlers = useRef({
    // The hover ×: the step and everything after it (confirm when that's more than one).
    onDelete: (nodeId: string) => {
      const count = collectSubtree(nodeId, latest.current.parentMap).size - 1;
      if (count === 0) {
        applyTree(removeSubtree(latest.current, nodeId));
        return;
      }
      const name = latest.current.nodes.find((n) => n.id === nodeId)?.data.label ?? "";
      setPendingRemoval({ id: nodeId, name, count });
    },
    onAddChild: (nodeId: string) => addStep(nodeId),
    onInsertBefore: (nodeId: string) => {
      const newId = generateNodeId();
      setParentMap((prev) => {
        const next = new Map(prev);
        next.set(newId, prev.get(nodeId) ?? null);
        next.set(nodeId, newId);
        return next;
      });
      setNodes((nds) => [...nds, newStepNode(newId, nds.length + 1, handlers.current)]);
    },
  });

  /** Show a saved workflow on the canvas and make it the unsaved-changes baseline. */
  const load = useCallback(
    (saved: FlowResponse): StepTree => {
      const tree = treeFromFlow(saved, (i) => `${t("editor.stepConfig")} ${i}`);
      const withHandlers = tree.nodes.map((n) => ({ ...n, data: { ...n.data, ...handlers.current } }));
      setNodes(withHandlers);
      setParentMap(tree.parentMap);
      setSavedSnapshot(treeSnapshot(tree));
      return tree;
    },
    [setNodes, t],
  );

  useEffect(() => {
    if (initialized.current || !flow.steps) return;
    initialized.current = true;
    load(flow);
  }, [flow, load]);

  // Relayout + rebuild edges whenever the tree's STRUCTURE changes. Depends on
  // parentMap and node count only — it calls setNodes to apply positions, so
  // depending on `nodes` itself would loop. Positions/handlers are reapplied
  // from the latest nodes inside the updater.
  const nodeCount = nodes.length;
  useEffect(() => {
    if (nodeCount === 0) {
      setEdges([]);
      return;
    }
    setNodes((current) => {
      const positions = computeTreeLayout(current, parentMap);
      const withLayout = current.map((n) => ({
        ...n,
        position: positions.get(n.id) ?? n.position,
        data: { ...n.data, ...handlers.current },
      }));
      setEdges(buildTreeEdges(withLayout, parentMap));
      return withLayout;
    });
    setTimeout(() => fitView({ padding: 0.2, duration: 200 }), 50);
  }, [parentMap, nodeCount, setEdges, setNodes, fitView]);

  const selected = nodes.find((n) => n.id === selectedId) ?? null;
  const updateSelected = (patch: Partial<StepNodeData>) => {
    if (!selected) return;
    setNodes((nds) => nds.map((n) => (n.id === selected.id ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const queryClient = useQueryClient();
  const saveMutation = useMutation(updateFlowMutation());
  const handleSave = async () => {
    const { steps, connectors } = serializeSteps(nodes, parentMap);
    const selectedIndex = selectedId ? saveOrder(parentMap).indexOf(selectedId) : -1;
    try {
      const res = await saveMutation.mutateAsync({
        path: { flow_uuid: flow.uuid },
        body: { steps: steps as FlowStepConfig[], connectors: connectors as unknown as FlowConnectorConfig[] },
        throwOnError: true,
      });
      // Rebuild from what the server saved, so the canvas is exactly the saved workflow.
      const saved = load(res.data);
      setSelectedId(selectedIndex >= 0 ? (saveOrder(saved.parentMap)[selectedIndex] ?? null) : null);
      await queryClient.invalidateQueries({ queryKey: getFlowQueryKey({ path: { flow_uuid: flow.uuid } }) });
      toast.success(t("editor.saved"));
    } catch {
      toast.error(t("editor.saveFailed"));
    }
  };

  return (
    <div className="flex h-[600px] overflow-hidden rounded-lg border border-border-input bg-surface-base">
      <div className="relative flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={(changes) => onNodesChange(changes.filter((c) => c.type !== "position"))}
          onNodeClick={(_, node) => {
            setSelectedId(node.id);
            setPickingAgent(false);
          }}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable={false}
          deleteKeyCode={null}
          nodesConnectable={false}
          proOptions={{ hideAttribution: true }}
          colorMode="system"
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <Controls />
          <StepsEditorPanels
            hasSteps={nodes.length > 0}
            dirty={dirty}
            saving={saveMutation.isPending}
            onAddStep={() => addStep(findLastLeaf(nodes, parentMap))}
            onSave={handleSave}
          />
        </ReactFlow>
        {nodes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <Text variant="body-sm" tone="tertiary">
              {t("editor.empty")}
            </Text>
          </div>
        )}
      </div>

      {selected && (
        <div className="w-80 overflow-y-auto border-l border-border-subtle bg-surface-primary p-4">
          {pickingAgent ? (
            <AgentPicker
              onAssign={(a) => {
                updateSelected({ agentUuid: a.uuid, agentName: a.name, label: selected.data.label || a.name });
                setPickingAgent(false);
              }}
              onClose={() => setPickingAgent(false)}
            />
          ) : (
            <StepConfig
              node={selected}
              parentMap={parentMap}
              nodes={nodes}
              onChange={updateSelected}
              onChangeParent={(parentId) => setParentMap((prev) => new Map(prev).set(selected.id, parentId))}
              onPickAgent={() => setPickingAgent(true)}
              onClearAgent={() => updateSelected({ agentUuid: undefined, agentName: undefined })}
              onDelete={() => applyTree(removeStep({ nodes, parentMap }, selected.id))}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingRemoval !== null}
        title={t("editor.removeConfirmTitle")}
        body={t("editor.removeConfirmBody", { name: pendingRemoval?.name ?? "", count: pendingRemoval?.count ?? 0 })}
        confirmLabel={t("editor.removeConfirmAction")}
        onConfirm={() => {
          if (pendingRemoval) applyTree(removeSubtree(latest.current, pendingRemoval.id));
          setPendingRemoval(null);
        }}
        onClose={() => setPendingRemoval(null)}
      />
      {leaveGuard}
    </div>
  );
}
